import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {TextBox} from '../../../../libs/shared/ui/src/components/boxedInputs/textBox';
import {StyledSettingsFormField} from '../../../../libs/shared/ui/src/components/settings/settingsWrapper';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {CircleModel} from '../../../models/api/circleModel';
import {DepartmentsEnum} from '../../../models/api/userMetadataModel';
import {InboxColorPicker} from '../../../settings/inboxes/inboxPreferences/inboxColorPicker';
import {SettingsInboxesNewCreatorState} from '../../../settings/inboxes/newCreator/settingsInboxesNewCreatorState';
import {
  newOnboardingSlideHeight,
  newOnboardingSlideWidth,
  StyledOnboardingFormTitleDiv,
  StyledOnboardingFormTitlesDiv,
} from './onboardingWelcomeHelpers';
import {OnboardingWelcomeSlide, OnboardingWelcomeSlideNavigationProps} from './onboardingWelcomeSlide';

/*
 * Constants.
 */

export const onboardingInboxAccessAll = 'ALL';

/*
 * Props.
 */

export interface OnboardingInboxSlideProps extends OnboardingWelcomeSlideNavigationProps {
  createdCircle: CircleModel | undefined;
  inboxState: SettingsInboxesNewCreatorState;
  onSetName: (name: string) => void;
  onSetColor: (color: string) => void;
  canSave: boolean;

  selectedTeamType: DepartmentsEnum | string;
}

export type OnboardingInboxAccess = typeof onboardingInboxAccessAll | number;

/*
 * Style.
 */

const StyledLeftDiv = styled.div`
  position: relative;
  grid-template-areas:
    'description'
    'form'
    'footer';

  flex: 0 0 100%;
`;

const StyledTitleDiv = styled.div`
  grid-area: title;

  width: 430px;
`;

const StyledDescriptionDiv = styled.div`
  grid-area: description;

  font-weight: ${fontWeights.medium};
  font-size: ${fontSizes.medium};
  color: ${(p) => p.theme.greys.shade70};
  line-height: 1.29;

  width: 456px;
`;

const StyledFieldsDiv = styled.div`
  padding-top: 32px;

  grid-area: form;
`;

const StyledColorFormFieldDiv = styled.div`
  display: flex;
  flex-direction: column;
  width: fit-content;
  gap: 12px;

  padding-top: 12px;
`;

const StyledSettingsLabelDiv = styled.div`
  font-weight: ${fontWeights.semibold};
  font-size: ${fontSizes.small};
  color: ${(p) => p.theme.greys.shade70};
  line-height: 16px;
`;

/*
 * Component.
 */

export const OnboardingInboxSlide: FC<OnboardingInboxSlideProps> = (props) => {
  const {createdCircle, inboxState, onSetName, onSetColor, selectedTeamType, navPrimary, navSecondary} =
    props;

  // State.
  const {name, color} = inboxState;

  const renderCustomSuggestions = () => {
    switch (selectedTeamType) {
      case DepartmentsEnum.ACCOUNT_MANAGEMENT:
        return (
          <Trans id="onboardinginbox-name-description-accountmanagement">
            e.g. Account Services, Contracts, New Business
          </Trans>
        );
      case DepartmentsEnum.BROKERAGE:
        return (
          <Trans id="onboardinginbox-name-description-brokerage">e.g. Client Inquiries, Data, Invoices</Trans>
        );
      case DepartmentsEnum.CLIENT_SERVICES:
        return (
          <Trans id="onboardinginbox-name-description-clientservices">
            e.g. Client Inquiries, Client Feedback, Priority
          </Trans>
        );
      case DepartmentsEnum.CUSTOMER_SUPPORT:
        return (
          <Trans id="onboardinginbox-name-description-customersupport">
            e.g. Customer Success, Escalations, Support Tier 1
          </Trans>
        );
      case DepartmentsEnum.DESIGN:
        return (
          <Trans id="onboardinginbox-name-description-design">
            e.g. Brand Requests, Customer Feedback, Research
          </Trans>
        );
      case DepartmentsEnum.ENGINEERING:
        return (
          <Trans id="onboardinginbox-name-description-engineering">
            e.g. Deliverability, On-call Alerts, Support Tier 1
          </Trans>
        );
      case DepartmentsEnum.EXECUTIVE:
        return (
          <Trans id="onboardinginbox-name-description-executive">
            e.g. Executive Assistant, Priority, Urgent
          </Trans>
        );
      case DepartmentsEnum.FINANCE:
        return (
          <Trans id="onboardinginbox-name-description-finance">
            e.g. Accounts Payable, Accounts Receivable, Invoices
          </Trans>
        );
      case DepartmentsEnum.LEGAL:
        return <Trans id="onboardinginbox-name-description-legal">e.g. Compliance, Contracts, Privacy</Trans>;
      case DepartmentsEnum.MARKETING:
        return (
          <Trans id="onboardinginbox-name-description-marketing">
            e.g. Marketing, Marketing Ops, Outbound Alerts
          </Trans>
        );
      case DepartmentsEnum.OPERATIONS:
        return (
          <Trans id="onboardinginbox-name-description-operations">e.g. Customer Ops, Requests, Urgent</Trans>
        );
      case DepartmentsEnum.PRODUCT:
        return (
          <Trans id="onboardinginbox-name-description-product">
            e.g. Customer Feedback, Product, Product Insights
          </Trans>
        );
      case DepartmentsEnum.PROJECT_MANAGEMENT:
        return (
          <Trans id="onboardinginbox-name-description-projectmanagement">
            e.g. Budget Updates, New Inquiries, Urgent
          </Trans>
        );
      case DepartmentsEnum.SALES:
        return <Trans id="onboardinginbox-name-description-sales">e.g. Leads, New Business, Sales</Trans>;
      default:
        return <Trans id="onboardinginbox-name-description-default">e.g. Feedback, Requests, Urgent</Trans>;
    }
  };

  return (
    <OnboardingWelcomeSlide
      width={newOnboardingSlideWidth}
      height={newOnboardingSlideHeight}
      navSecondary={navSecondary}
      navPrimary={navPrimary}
      isLeftContent
    >
      <StyledLeftDiv>
        {/* Title */}
        <StyledTitleDiv>
          <StyledOnboardingFormTitlesDiv>
            <StyledOnboardingFormTitleDiv>
              {createdCircle ? (
                <Trans id="onboardinginbox-title-teammategroup">
                  Next, create a shared inbox for this teammate group
                </Trans>
              ) : (
                <Trans id="onboardinginbox-title">Next, create a shared inbox</Trans>
              )}
            </StyledOnboardingFormTitleDiv>
          </StyledOnboardingFormTitlesDiv>
        </StyledTitleDiv>
        {/* Description */}
        <StyledDescriptionDiv>
          <Trans id="onboardinginbox-description">
            An inbox is a centralized space for your team to access messages coming from multiple channels
            (i.e. email, SMS, Facebook, etc.).
          </Trans>
        </StyledDescriptionDiv>
        {/* Fields */}
        <StyledFieldsDiv>
          <StyledSettingsFormField>
            <TextBox
              label={<Trans id="onboardinginbox-name">Inbox name</Trans>}
              value={name}
              onChange={onSetName}
              message={renderCustomSuggestions()}
              placeholder="Add inbox name"
              isRequired
              shouldAutoFocus
            />
          </StyledSettingsFormField>
          {/* after the inbox is created, we'll have to do a patch on whatever color is selected here. */}
          <StyledColorFormFieldDiv>
            <StyledSettingsLabelDiv>
              <Trans id="onboardinginbox-color">Color</Trans>
            </StyledSettingsLabelDiv>
            <InboxColorPicker color={color ?? 'grey'} onSetColor={(c) => onSetColor(c)} />
          </StyledColorFormFieldDiv>
        </StyledFieldsDiv>
      </StyledLeftDiv>
    </OnboardingWelcomeSlide>
  );
};
